// Texas A&M University: public college page (#2740), official facts only.
//
// Every figure below was read on 2026-10-01 from the source it names:
//  - Texas A&M's Common Data Set 2025-2026 (Academic and Business
//    Performance Analytics), published as a web page on abpa.tamu.edu (no
//    PDF; the page says a printable copy is by email request), read in a
//    browser: C1 applicants, admits and enrollees for Fall 2025 with the
//    residency table (the Unknown column is all zero and left out), C8 test
//    policy for Fall 2027 and the score date, C21/C22, G1/G5 costs for
//    2026-2027 (section G is not marked as unavailable). C22 marks a
//    nonbinding early action plan but gives only "October 15th", with no
//    closing or notification date and no counts; the page says what each
//    source states.
//  - admissions.tamu.edu: Freshman (fall deadlines, decision timing, test
//    policy, engineering review, Top 10% admission) and the Test-Optional
//    Admissions FAQs on its College Readiness page.
//  - College Scorecard API, school id 228723 (net price by family income).
// Nothing here is profile-derived, an odds figure or a student record.
// Re-check a figure against its source before editing it, and move
// `lastChecked` when you do.

export const SOURCES = {
    cds: {
        label: 'Texas A&M Common Data Set 2025-2026',
        url: 'https://abpa.tamu.edu/accountability-rankings/common-data-set.html',
        read: '2026-10-01',
    },
    freshman: {
        label: 'Texas A&M Office of Admissions, Freshman',
        url: 'https://admissions.tamu.edu/apply/freshman/index.html',
        read: '2026-10-01',
    },
    testFaq: {
        label: 'Texas A&M Office of Admissions, Test-Optional Admissions FAQs',
        url: 'https://admissions.tamu.edu/resources/future-students/college-readiness.html',
        read: '2026-10-01',
    },
    scorecard: {
        label: 'College Scorecard, U.S. Department of Education',
        url: 'https://collegescorecard.ed.gov/school/?228723-Texas-A-M-University-College-Station',
        read: '2026-10-15',
    },
};

const college = {
    slug: 'texas-a-m-university',
    kbId: 'texas_a_m_university',
    name: 'Texas A&M University',
    shortName: 'Texas A&M',
    aliases: ['TAMU', 'Texas A&M', 'A&M'],
    location: 'College Station, TX',
    control: 'Public',
    lastChecked: '2026-10-01',
    sources: SOURCES,
    // From the KB's sourced branding (Texas A&M Marketing and Communications brand colors); decoration only.
    branding: {
        primary: '#500000',
        secondary: '#FFFFFF',
        logo_url: 'https://storage.googleapis.com/college-counselor-assets/university-logos/texas_a_m_university_logo.png',
    },

    // The direct answer and short FAQ (#4229): every figure restates one the
    // page already shows, from the source named beside it.
    // The search it answers: a&m acceptance rate 2027.
    faq: {
        answer: { text: 'Texas A&M University admitted 51.7% of first-year applicants for fall 2025, 32,531 of the 62,967 who applied, according to its Common Data Set 2025-2026.', source: 'cds', where: 'section C1' },
        questions: [
            { q: 'What is the Texas A&M acceptance rate for fall 2027?', a: 'There isn\'t one yet: students entering in fall 2027 are applying now. The newest rate in Texas A&M\'s Common Data Set is for fall 2025, when it admitted 32,531 of 62,967 first-year applicants, 51.7%, and 12,484 enrolled.', source: 'cds', where: 'section C1' },
            { q: 'What is the Texas A&M acceptance rate for out-of-state students?', a: 'For fall 2025, Texas A&M admitted 4,489 of 10,882 out-of-state first-year applicants, 41.3%. Texas residents were admitted at 54.2%, 26,620 of 49,156.', source: 'cds', where: 'section C1, residency table' },
            { q: 'Do I need to take the SAT or ACT for Texas A&M?', a: 'No. Texas A&M is test optional, so freshman applicants are not required to provide SAT or ACT scores. It encourages you to send them if you have them; they are used only to benefit you, and you are not penalized for low scores or no scores.', source: 'testFaq' },
            { q: 'Does Texas A&M superscore?', a: 'No. Texas A&M does not superscore the SAT or ACT.', source: 'testFaq' },
            { q: 'What is the Texas A&M application deadline?', a: 'For fall 2027 entry at College Station, apply by December 1 and send your documents, including your STARS record, by December 15. Most decisions come between January 1 and late March.', source: 'freshman' },
        ],
    },

    admission: {
        entering: 'Fall 2025',
        cdsCycle: '2025-2026',
        applied: 62967,
        admitted: 32531,
        enrolled: 12484,
        source: 'cds',
        where: 'section C1',
        residency: [
            { group: 'In-state', applied: 49156, admitted: 26620 },
            { group: 'Out-of-state', applied: 10882, admitted: 4489 },
            { group: 'International', applied: 2929, admitted: 1422 },
        ],
        residencySource: 'cds',
        residencyWhere: 'section C1, residency table',
    },
    // Acceptance by school or major: shown only where the school publishes it.
    byMajor: {
        published: false,
        note: 'Texas A&M\'s Common Data Set reports one first-year acceptance rate for the whole university, with no rate by college or major.',
        source: 'cds',
    },
    earlyRounds: {
        title: 'Early plans',
        note: 'Texas A&M has no early decision plan. The Common Data Set marks a nonbinding, not restrictive early action plan but gives only "October 15th", with no closing or notification date and no counts. The admissions pages list one fall deadline, December 1, with decisions released throughout the cycle; October 15 is the spring entry deadline.',
        source: 'cds',
        where: 'sections C21 and C22',
    },

    programs: {
        entering: 'Fall 2027',
        intro: 'Engineering has its own review:',
        rows: [
            { school: 'College of Engineering', requirement: 'Every applicant to the College of Engineering is reviewed for placement into General Engineering, including students who qualify for Top 10% automatic admission. This includes Biological and Agricultural Engineering, Computer Science, Industrial Distribution and Engineering Technology. Engineering pathway offers begin in January and may include direct admission to Engineering at Blinn, Galveston or McAllen.' },
        ],
        source: 'freshman',
    },

    deadlines: {
        entering: 'Fall 2027',
        headline: { label: 'Application deadline', value: 'December 1' },
        rows: [
            { plan: 'Fall application (College Station)', deadline: 'December 1', decision: 'Most between January 1 and late March', binding: false },
        ],
        note: 'Required and optional documents, including your STARS record (the course and grade record that stands in for your transcript), are due December 15. Decisions are made throughout the cycle, and applying early does not guarantee an early decision. Texas residents in the top 10% of their class are admitted automatically and usually hear within three weeks of completing their file; for Texas residents who completed homeschooling, that rank is assigned from scores (1290 SAT or 27 ACT for students applying for admission beginning Fall 2027). Admitted students accept by May 1. A deadline on a weekend stays open until the following Monday.',
        source: 'freshman',
    },

    testing: {
        entering: 'Fall 2027',
        policy: 'Test-optional',
        blind: false,
        line: 'Texas A&M is test optional: freshman applicants are not required to provide SAT or ACT scores. It encourages you to send scores if you have them; they are used only to benefit you, and you are not penalized for low scores or for no scores. Texas A&M does not superscore.',
        source: 'testFaq',
        cdsLine: 'The Common Data Set also marks the SAT or ACT as considered if submitted, not required, for fall 2027 applicants, with scores due by December 1.',
        cdsWhere: 'section C8',
    },

    // The next cycle (2027-28, fall 2028 entry): only what the school has
    // announced, read on the date below; nothing announced says so (#2651).
    nextCycle: {
        entering: 'Fall 2028',
        checked: '2026-10-01',
        changes: [],
        source: 'freshman',
    },

    cost: {
        year: '2026-2027',
        columns: ['In-state', 'Out-of-state'],
        rows: [
            { item: 'Tuition (first-year)', amounts: [8886, 36877] },
            { item: 'Required fees', amounts: [3970, 3970] },
            { item: 'Housing (on campus)', amounts: [8844, 8844] },
            { item: 'Food (on-campus meal plan)', amounts: [4100, 4100] },
            { item: 'Books and supplies', amounts: [882, 882] },
            { item: 'Transportation', amounts: [1692, 1692] },
            { item: 'Other expenses', amounts: [3252, 3252] },
        ],
        totals: [31626, 59617],
        note: 'For a full-time first-year student living on campus. Texas A&M notes that tuition and fees vary by undergraduate program.',
        source: 'cds',
        where: 'sections G1, G4 and G5',
        netPrice: {
            average: 21527,
            byIncome: [
                { income: '$0 to $30,000', amount: 13226 },
                { income: '$30,001 to $48,000', amount: 13602 },
                { income: '$48,001 to $75,000', amount: 16811 },
                { income: '$75,001 to $110,000', amount: 25126 },
                { income: 'Over $110,000', amount: 30748 },
            ],
            note: 'What first-year, full-time, in-state students who received federal aid paid after grants and scholarships, by family income, in 2024-25, the latest year the Department of Education publishes. Your own price comes from Texas A&M\'s aid offer.',
            tileDetail: 'Per year, after grants, in-state federal aid recipients',
            source: 'scorecard',
        },
    },
};

export default college;
